import type { SignalStrength } from "@/lib/db-types";

/**
 * Katalogen over signaltyper (CRM-3.3). `signal_agents.signal_type` og
 * `customer_signals.signal_type` er nøkler her. Systemagentene i migrasjonen
 * `crm_signal_agents_seed` bruker bare disse nøklene (testet mot filen).
 *
 * `deterministic`: signalet lages av kode/SQL (Brønnøysund-synk eller
 * crm_run_internal_signals), ikke av Claude. Slike agenter tas ikke med i
 * Claude-prompten i run-signal-agents. Speilet i
 * supabase/functions/_shared/signal-catalog.ts (testet likt).
 */
export type SignalTypeKey =
  | "job_posting"
  | "new_leader"
  | "new_product"
  | "event_announced"
  | "strong_growth"
  | "anniversary"
  | "social_campaign_no_video"
  | "delivered_12_months"
  | "new_subsidiary"
  | "media_mention"
  | "company_risk"
  | "custom";

export interface SignalTypeMeta {
  label: string;
  /** Standard kontaktårsak, brukt som tema på prosessen når signalet tas. */
  contactReason: string;
  strength: SignalStrength;
  /** Navn i SrIcon (sprite eller Lucide). */
  icon: string;
  deterministic: boolean;
}

export const SIGNAL_TYPES: Record<SignalTypeKey, SignalTypeMeta> = {
  job_posting: { label: "Ny stilling utlyst", contactReason: "Rekrutteringsfilm", strength: "medium", icon: "briefcase", deterministic: false },
  new_leader: { label: "Ny leder", contactReason: "Ny leder, ny start", strength: "medium", icon: "user", deterministic: false },
  new_product: { label: "Nytt produkt eller tjeneste", contactReason: "Lanseringsfilm", strength: "medium", icon: "sparkles", deterministic: false },
  event_announced: { label: "Arrangement annonsert", contactReason: "Arrangementsfilm", strength: "medium", icon: "calendar", deterministic: false },
  strong_growth: { label: "Sterk vekst", contactReason: "Vekst og historiefortelling", strength: "medium", icon: "trending-up", deterministic: true },
  anniversary: { label: "Jubileum", contactReason: "Jubileumsfilm", strength: "low", icon: "award", deterministic: true },
  social_campaign_no_video: { label: "Kampanje uten video", contactReason: "Videoinnhold til kampanje", strength: "medium", icon: "megaphone", deterministic: false },
  delivered_12_months: { label: "Levert for ett år siden", contactReason: "Oppfølging etter leveranse", strength: "medium", icon: "clock", deterministic: true },
  new_subsidiary: { label: "Nytt datterselskap", contactReason: "Nytt selskap trenger film", strength: "medium", icon: "users", deterministic: false },
  media_mention: { label: "Omtale i media", contactReason: "Omtale kan bli film", strength: "low", icon: "message-square", deterministic: false },
  company_risk: { label: "Advarsel", contactReason: "Vurder utestående", strength: "high", icon: "alert", deterministic: true },
  custom: { label: "Egendefinert", contactReason: "", strength: "medium", icon: "star", deterministic: false },
};

export const SIGNAL_TYPE_KEYS = Object.keys(SIGNAL_TYPES) as SignalTypeKey[];

export function isSignalTypeKey(key: string): key is SignalTypeKey {
  return key in SIGNAL_TYPES;
}

/** Visningsnavn for en lagret type; ukjente nøkler vises som de er. */
export function signalTypeLabel(key: string): string {
  return isSignalTypeKey(key) ? SIGNAL_TYPES[key].label : key;
}

export const SIGNAL_SOURCE_LABELS: Record<string, string> = {
  website: "Nettside",
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  brreg: "Brønnøysund",
  news: "Nyheter",
  revehiet: "Revehiet",
};

export function signalSourceLabel(source: string): string {
  return SIGNAL_SOURCE_LABELS[source] ?? source;
}

export const SIGNAL_STRENGTH_LABELS: Record<SignalStrength, string> = {
  high: "Sterk",
  medium: "Middels",
  low: "Svak",
};

/** Kort kildemerke på kundekortet (LI, WEB, IG, FB, BR). Ukjent kilde får de tre første bokstavene. */
export const SIGNAL_SOURCE_CHIPS: Record<string, string> = {
  website: "WEB",
  instagram: "IG",
  facebook: "FB",
  linkedin: "LI",
  brreg: "BR",
  news: "NYH",
  revehiet: "RH",
};

export function signalSourceChip(source: string): string {
  return SIGNAL_SOURCE_CHIPS[source] ?? source.slice(0, 3).toUpperCase();
}
